import { cn } from "@/lib/utils";
import { useDropzone } from "react-dropzone";
import { useCallback, useEffect, useState } from "react";
import styles from "./file-dropzone.module.css";
import { FilePlus2 } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * The props for the FileDropzone component.
 */
interface FileDropzoneProps {
    /**
     * The width of the dropzone.
     */
    width: number;
    /**
     * The height of the dropzone.
     */
    height: number;
    /**
     * A callback function that is called when images are dropped onto the canvas.
     */
    onImagesDrop: (files: File[]) => void;
}

/**
 * A component that allows users to drop images onto the canvas.
 * @param width - The width of the dropzone.
 * @param height - The height of the dropzone.
 * @param onImagesDrop - A callback function that is called when images are dropped onto the canvas.
 * @returns A component that allows users to drop images onto the canvas.
 */
export const FileDropzone = ({ width, height, onImagesDrop }: FileDropzoneProps) => {
    const { t } = useTranslation();
    const [isDraggingFiles, setIsDraggingFiles] = useState(false);

    const onDrop = useCallback(
        (files: File[]) => {
            if (onImagesDrop) {
                onImagesDrop(files);
            }
            setIsDraggingFiles(false);
        },
        [onImagesDrop],
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            "image/*": [],
        },
        multiple: true,
        noClick: true,
        noKeyboard: true,
    });

    // Track global drag events to detect when files are being dragged
    useEffect(() => {
        const handleDragOver = (e: DragEvent) => {
            e.preventDefault();
            if (e.dataTransfer?.types.includes("Files")) {
                setIsDraggingFiles(true);
            }
        };

        const handleDragLeave = (e: DragEvent) => {
            e.preventDefault();
            // Only set to false if we're leaving the document
            if (e.relatedTarget === null) {
                setIsDraggingFiles(false);
            }
        };

        const handleDrop = () => {
            setIsDraggingFiles(false);
        };

        window.addEventListener("dragover", handleDragOver);
        window.addEventListener("dragleave", handleDragLeave);
        window.addEventListener("drop", handleDrop);

        return () => {
            window.removeEventListener("dragover", handleDragOver);
            window.removeEventListener("dragleave", handleDragLeave);
            window.removeEventListener("drop", handleDrop);
        };
    }, []);

    return (
        <div
            {...getRootProps()}
            className={cn(
                getRootProps().className,
                styles.fileDropzone,
                (isDragActive || isDraggingFiles) && styles.active,
            )}
            style={{ width, height }}
        >
            <input {...getInputProps()} />
            {(isDragActive || isDraggingFiles) && (
                <div className="flex flex-col items-center justify-center gap-2">
                    <FilePlus2 className="w-4 h-4" />
                    <p>{t("templates.editor.dropImagesHere")}</p>
                </div>
            )}
        </div>
    );
};
